import { urlGestionPagadores } from "../api/urls";

/**
 * Service to handle pagadores management via backend.
 * Abstraction layer to keep component clean.
 */

const handleResponse = async (response) => {
    if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(errorBody || "Error en la petición al servidor");
    }
    const data = await response.json();
    return data;
};

const headers = {
    "Content-Type": "application/json",
    "Accept": "application/json",
};

export const pagadoresService = {
    /**
     * Obtiene la lista de pagadores del carrito
     * @param {string} renovacionId 
     * @returns {Promise<Array>} Lista de pagadores
     */
    obtenerPagadores: async (renovacionId) => {
        try {
            const url = `${urlGestionPagadores}${renovacionId}`;
            // GET request sends accion in query or body? 
            // Backend: if (!empty($body)) switch($body->accion).
            // Usually GET requests don't have body. 
            // If backend STRICTLY checks $body, we might need POST for GET too.
            // But 'GET' case usually implies HTTP GET. 
            // However, prompt says: "Las acciones disponibles son: ... GET". 
            // And "if (!empty($body))". This strongly suggests passing { accion: 'GET' } in BODY.
            // So I will use POST for everything to be safe with this specific backend logic described.

            const response = await fetch(url, {
                method: "POST",
                body: JSON.stringify({ accion: "GET" }),
            });

            const data = await handleResponse(response);

            if (data.pagadores && Array.isArray(data.pagadores)) {
                return data.pagadores.map(p => ({
                    identificacion: p.cliIdentificacion || p.identificacion || "",
                    cliNombres: p.cliNombre1 || p.cliNombres || "",
                    cliApellidos: p.cliApellido2 || p.cliApellidos || "",
                    direccion: p.cliDireccion || p.direccion || "",
                    celular: p.cliTelefono || p.celular || "",
                    participacion: p.cliParticipacion || p.participacion || 0,
                    correo: p.cliEmail || p.correo || "",
                    fechaNacimiento: (p.cliFechaNacimiento || p.fechaNacimiento) ? new Date(p.cliFechaNacimiento || p.fechaNacimiento) : null,
                    codTipodocumento: p.cliTipoDocumento || p.codTipodocumento || "",
                    id: p._id || p.id || "",
                }));
            }
            return [];
        } catch (error) {
            console.error("Error al obtener pagadores:", error);
            throw error;
        }
    },

    /**
     * Agrega un nuevo pagador
     * @param {string} renovacionId 
     * @param {object} pagador 
     * @returns {Promise<object>} Respuesta del backend
     */
    agregarPagador: async (renovacionId, pagador) => {
        try {
            const url = `${urlGestionPagadores}${renovacionId}`;
            const response = await fetch(url, {
                method: "POST",
                body: JSON.stringify({
                    accion: "ADD",
                    numeroIdentificacion: pagador.identificacion,
                    nombreRazonSocial: pagador.cliNombres,
                    apellidos: pagador.cliApellidos || "",
                    correoElectronico: pagador.correo,
                    fechaNacimientoConstitucion: pagador.fechaNacimiento,
                    tipoDocumento: pagador.codTipodocumento,
                    direccion: pagador.direccion,
                    celular: pagador.celular,
                    participacion: pagador.participacion
                }),
            });
            return await handleResponse(response);
        } catch (error) {
            console.error("Error al agregar pagador:", error);
            throw error;
        }
    },

    /**
     * Modifica un pagador existente
     * @param {string} renovacionId 
     * @param {object} pagador 
     * @returns {Promise<object>} Respuesta del backend
     */
    modificarPagador: async (renovacionId, pagador) => {
        try {
            const baseUrl = `${urlGestionPagadores}${renovacionId}`;
            const url = baseUrl.includes('?')
                ? `${baseUrl}&cedulaPagador=${pagador.identificacion}`
                : `${baseUrl}?cedulaPagador=${pagador.identificacion}`;

            const response = await fetch(url, {
                method: "POST",
                body: JSON.stringify({
                    accion: "MOD",
                    numeroIdentificacion: pagador.identificacion,
                    nombreRazonSocial: pagador.cliNombres,
                    nombres: pagador.cliNombres,
                    apellidos: pagador.cliApellidos || "",
                    correoElectronico: pagador.correo,
                    fechaNacimientoConstitucion: pagador.fechaNacimiento,
                    tipoDocumento: pagador.codTipodocumento,
                    direccion: pagador.direccion,
                    celular: pagador.celular,
                    participacion: pagador.participacion
                }),
            });
            return await handleResponse(response);
        } catch (error) {
            console.error("Error al modificar pagador:", error);
            throw error;
        }
    },

    /**
     * Elimina un pagador
     * @param {string} renovacionId 
     * @param {string} identificacion 
     * @returns {Promise<object>} Respuesta del backend
     */
    eliminarPagador: async (renovacionId, identificacion) => {
        try {
            const baseUrl = `${urlGestionPagadores}${renovacionId}`;
            const url = baseUrl.includes('?')
                ? `${baseUrl}&cedulaPagador=${identificacion}`
                : `${baseUrl}?cedulaPagador=${identificacion}`;

            const response = await fetch(url, {
                method: "POST",
                body: JSON.stringify({
                    accion: "DEL"
                }),
            });
            return await handleResponse(response);
        } catch (error) {
            console.error("Error al eliminar pagador:", error);
            throw error;
        }
    }
};
